import { Button } from '@timelyapp/react';
import { DollarCircle, InfoIcon, Task } from 'components/Icons';
import InlineMessage from 'components/InlineMessage';
import ConfirmationDialogue from 'components/modals/ConfirmationDialogue';
import TypeToConfirm from 'components/TypeToConfirm';
import React, { useState } from 'react';
import FontAwesome from 'react-fontawesome';
import { useSelector } from 'react-redux';
import { getProjectById } from 'redux/selectors/projects';
import { getFullProjectOrNull } from 'redux/selectors/utils/projects';
import { getProjectColor } from 'utils/project';
import { ucFirst } from 'utils/text-helper';
import ProjectBadge from './ProjectBadge';
import styles from './ProjectDeleteConfirmationDialogue.module.scss';

export type ProjectActionDetails = {
  id: number;
  name?: string;
  intent: 'archive' | 'delete';
} | null;

export type BulkDeleteProject = { id: number; name: string };

interface Props {
  projectActionDetails: ProjectActionDetails;
  setProjectActionDetails: (projectActionDetails: ProjectActionDetails) => void;
  handleDeleteProject: () => void;
  handleArchiveProject: () => void;
  bulkDeleteProjects?: BulkDeleteProject[];
  onBulkDelete?: () => void;
  onCancelBulkDelete?: () => void;
}

const ProjectDeleteConfirmationDialogue: React.FC<Props> = ({
  projectActionDetails,
  setProjectActionDetails,
  handleDeleteProject,
  handleArchiveProject,
  bulkDeleteProjects,
  onBulkDelete,
  onCancelBulkDelete,
}) => {
  const [hasTypedToConfirm, setHasTypedToConfirm] = useState(false);
  const projectById = useSelector(getProjectById)[projectActionDetails?.id ?? 0];
  const project = getFullProjectOrNull(projectById);

  if (bulkDeleteProjects && bulkDeleteProjects.length > 0) {
    const count = bulkDeleteProjects.length;
    return (
      <ConfirmationDialogue
        title={`Delete ${count} ${count === 1 ? 'project' : 'projects'}`}
        buttons={[
          {
            text: "Cancel, don't delete",
            priority: 'medium',
            onClick: onCancelBulkDelete,
          },
        ]}
        isOpen
        onClose={onCancelBulkDelete}
        width={600}
      >
        <div className={styles.warningContent}>
          <InlineMessage type={hasTypedToConfirm ? 'alert' : 'neutral'}>
            <ul className={styles.projectsList}>
              {bulkDeleteProjects.map(p => (
                <li key={p.id}>{p.name}</li>
              ))}
            </ul>
            <ul className={styles.consequencesList}>
              <li>
                <span className={styles.iconWrap}>
                  <FontAwesome style={{ marginRight: 5, color: '#697077' }} name="clock" />
                </span>
                <p>
                  <strong>All entries</strong> logged to these projects will be permanently deleted
                </p>
              </li>
              <li>
                <span className={styles.iconWrap}>
                  <Task fill="#697077" />
                </span>
                <p>
                  <strong>All tasks</strong> assigned to these projects will be permanently deleted
                </p>
              </li>
              <li>
                <span className={styles.iconWrap}>
                  <DollarCircle fill="#697077" color="white" />
                </span>
                <p>
                  <strong>All invoices</strong> based on these projects will be permanently deleted
                </p>
              </li>
              <li>
                <span className={styles.iconWrap}>
                  <InfoIcon backgroundColor="#697077" color="white" />
                </span>
                <p>
                  <strong>All reports</strong> that include these projects will change
                </p>
              </li>
            </ul>
            <div className={styles.confirmWrap}>
              <p>Type DELETE to confirm</p>
              <TypeToConfirm
                confirmString="DELETE"
                onConfirm={onBulkDelete!}
                onChange={setHasTypedToConfirm}
                confirmButtonProps={{
                  text: `Permanently delete ${count} ${count === 1 ? 'project' : 'projects'}`,
                  icon: <FontAwesome style={{ marginRight: 5 }} name="trash" />,
                  priority: 'high',
                  destructive: true,
                  'data-testid': 'confirm-bulk-delete-projects-button',
                }}
              />
            </div>
          </InlineMessage>
        </div>
      </ConfirmationDialogue>
    );
  }

  if (!projectActionDetails) {
    return null;
  }

  if (projectActionDetails.intent === 'archive') {
    return (
      <ConfirmationDialogue
        title={`Archive ${projectActionDetails.name!}`}
        buttons={[
          {
            text: "Cancel, don't archive",
            priority: 'medium',
            onClick: () => setProjectActionDetails(null),
          },
          {
            text: 'Permanently delete project and all records',
            priority: 'medium',
            onClick: () => setProjectActionDetails({ ...projectActionDetails, intent: 'delete' }),
            hasIcon: true,
            icon: <FontAwesome name="trash" />,
          },
        ]}
        isOpen={projectActionDetails != null}
        onClose={() => setProjectActionDetails(null)}
        width={600}
      >
        <div className={styles.warningContent}>
          <InlineMessage type="neutral">
            <ul className={styles.consequencesList}>
              <li>
                <span className={styles.projectBadgeWrap}>
                  {project && (
                    <ProjectBadge color={`#${getProjectColor(project)}`} ticMeta={project.tic} />
                  )}
                </span>
                <p>
                  <strong>{projectActionDetails.name}</strong> will be moved to archived projects
                </p>
              </li>
              <li>
                <span className={styles.iconWrap}>
                  <FontAwesome style={{ marginRight: 5, color: '#697077' }} name="clock" />
                </span>
                <p>This project cannot be selected when logging time</p>
              </li>
              <li>
                <span className={styles.iconWrap}>
                  <Task fill="#697077" />
                </span>
                <p>New tasks can no longer be added to this project</p>
              </li>
              <li>
                <span className={styles.iconWrap}>
                  <InfoIcon backgroundColor="#697077" color="white" />
                </span>
                <p>This project can be reactivated at any time</p>
              </li>
              {project?.tic?.uri && (
                <li>
                  <span className={styles.iconWrap}>
                    {project.tic.icon ? (
                      <img src={project.tic.icon} alt="" style={{ width: 14, height: 14 }} />
                    ) : (
                      <FontAwesome
                        style={{ marginRight: 5, color: '#697077' }}
                        name="external-link"
                      />
                    )}
                  </span>
                  <p>
                    The original in {ucFirst(project.tic.tool_id || 'external tool')} will not be
                    affected
                  </p>
                </li>
              )}
            </ul>
            <div className={styles.confirmWrap}>
              <Button
                priority="xhigh"
                onClick={handleArchiveProject}
                data-testid="confirm-archive-project-button"
              >
                <FontAwesome style={{ marginRight: 5 }} name="archive" />
                Archive project
              </Button>
            </div>
          </InlineMessage>
        </div>
      </ConfirmationDialogue>
    );
  }

  return (
    <ConfirmationDialogue
      title={`Delete ${projectActionDetails.name!}`}
      buttons={[
        {
          text: "Cancel, don't delete",
          priority: 'medium',
          onClick: () => setProjectActionDetails(null),
        },
        {
          text: 'Archive and keep all records',
          priority: 'medium',
          onClick: () => setProjectActionDetails({ ...projectActionDetails, intent: 'archive' }),
          hasIcon: true,
          icon: <FontAwesome name="archive" />,
        },
      ]}
      isOpen={projectActionDetails != null}
      onClose={() => setProjectActionDetails(null)}
      width={600}
    >
      <div className={styles.warningContent}>
        <InlineMessage type={hasTypedToConfirm ? 'alert' : 'neutral'}>
          <ul className={styles.consequencesList}>
            <li>
              <span className={styles.projectBadgeWrap}>
                {project && (
                  <ProjectBadge color={`#${getProjectColor(project)}`} ticMeta={project.tic} />
                )}
              </span>
              <p>
                <strong>{projectActionDetails.name}</strong> will be permanently deleted
              </p>
            </li>
            <li>
              <span className={styles.iconWrap}>
                <FontAwesome style={{ marginRight: 5, color: '#697077' }} name="clock" />
              </span>
              <p>
                <strong>All entries</strong> logged to this project will be permanently deleted
              </p>
            </li>
            <li>
              <span className={styles.iconWrap}>
                <Task fill="#697077" />
              </span>
              <p>
                <strong>All tasks</strong> assigned to this project will be permanently deleted
              </p>
            </li>
            <li>
              <span className={styles.iconWrap}>
                <DollarCircle fill="#697077" color="white" />
              </span>
              <p>
                <strong>All invoices</strong> based on this project will be permanently deleted
              </p>
            </li>
            <li>
              <span className={styles.iconWrap}>
                <InfoIcon backgroundColor="#697077" color="white" />
              </span>
              <p>
                <strong>All reports</strong> that include this project will change
              </p>
            </li>
            {project?.tic?.uri && (
              <li>
                <span className={styles.iconWrap}>
                  {project.tic.icon ? (
                    <img src={project.tic.icon} alt="" style={{ width: 14, height: 14 }} />
                  ) : (
                    <FontAwesome
                      style={{ marginRight: 5, color: '#697077' }}
                      name="external-link"
                    />
                  )}
                </span>
                <p>
                  The original in {ucFirst(project.tic.tool_id || 'external tool')} will not be
                  affected
                </p>
              </li>
            )}
          </ul>
          <div className={styles.confirmWrap}>
            <p>Type DELETE to confirm</p>
            <TypeToConfirm
              confirmString="DELETE"
              onConfirm={handleDeleteProject}
              onChange={setHasTypedToConfirm}
              confirmButtonProps={{
                text: 'Permanently delete project',
                icon: <FontAwesome style={{ marginRight: 5 }} name="trash" />,
                priority: 'high',
                destructive: true,
                'data-testid': 'confirm-delete-project-button',
              }}
            />
          </div>
        </InlineMessage>
      </div>
    </ConfirmationDialogue>
  );
};

export default ProjectDeleteConfirmationDialogue;
